import React, { useState } from 'react'
import { Formik } from 'formik'
import * as yup from 'yup'
import decode from 'jwt-decode'
import { formUtils } from '../../../../../utils'
import { FormField, FormLoader, TextField, TextInput } from '../../../../components'
import styles from './ResetPasswordForm.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { userApiEvents } from '../../../../../modules/api/user'
import { isEmpty, mergeRight, pathOr } from 'ramda'
import { apiStateSelectors } from '../../../../../modules/api/apiState'
import FormErrors from '../../../molecules/FormErrors/FormErrors'
import { TextFieldTypes } from 'components/atoms/TextField/text-field.types'
import { passwordConstraintContent, getMatchedPasswordCriteria } from '../passwordUtils'
import SvgEyeFill from 'views/components/atoms/Icons/Controls/Eye'
import SvgEyeSlashFill from 'views/components/atoms/Icons/Controls/EyeSlash'

interface ResetPasswordFormProps {
  token: string
}

const validationSchema = yup.object().shape({
  password: yup
    .string()
    .required('required.')
    .min(12, 'must be at least 12 characters.')
    .matches(/[a-z]/, 'must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'must contain at least one uppercase letter')
    .matches(/[0-9]/, 'must contain at least one number')
    .matches(/[!@#$%^&*(),.?":{}|<>]/, 'must contain at least one special character'),
  passwordConfirmation: yup
    .string()
    .oneOf([yup.ref('password'), null], "doesn't match password.")
    .required('required.'),
})

const ButtonContainer = ({ children }: any): React.ReactElement => (
  <div className={styles['submit-button']}>{children}</div>
)

const ResetPasswordForm = ({ token }: ResetPasswordFormProps): React.ReactElement => {
  const dispatch = useDispatch()
  const decodedToken = decode(token)
  const email = pathOr('', ['value', 'email'], decodedToken)

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const togglePasswordVisibility = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setShowPassword(!showPassword)
  }

  const toggleConfirmPasswordVisibility = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setShowConfirmPassword(!showConfirmPassword)
  }

  const isSubmitting = useSelector(state => apiStateSelectors.isLoading(state)([userApiEvents.passwordReset_request]))
  const getGeneralFormErrorsByEvent = useSelector(apiStateSelectors.getGeneralFormErrorsByEvent)
  const getFormFieldErrorsByEvent = useSelector(apiStateSelectors.getFormFieldErrorsByEvent)

  return (
    <div className={styles.root}>
      <h2>Reset your password</h2>
      <p>
        Set a new password for {email}
        <br />
        <a
          href="https://help.reos.co.za/en/articles/13884289-creating-a-strong-password"
          target="_blank"
          rel="noopener noreferrer"
        >
          Tips for creating a strong password
        </a>
      </p>
      <Formik
        enableReinitialize
        validationSchema={validationSchema}
        initialValues={{ password: '', passwordConfirmation: '' }}
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
        onSubmit={payload => dispatch(userApiEvents.passwordReset_request({ payload, token }))}
      >
        {({ handleSubmit, handleChange, handleBlur, submitCount, touched, values, errors }) => {
          const resetEvent = userApiEvents.passwordReset_request({ payload: values, token })
          const generalErrors = getGeneralFormErrorsByEvent(resetEvent)
          const fieldErrors = getFormFieldErrorsByEvent(resetEvent)
          const hasApiErrors = generalErrors.length > 0 || !isEmpty(fieldErrors)
          const getFieldError = formUtils.getFieldError(submitCount, touched, mergeRight(errors, fieldErrors))

          const formLoaderState = isSubmitting
            ? 'submitting'
            : (submitCount > 0 && !isEmpty(errors)) || hasApiErrors
            ? 'error'
            : undefined

          const matchedCriteria = getMatchedPasswordCriteria(values.password)

          return (
            <FormLoader
              state={formLoaderState}
              onSubmit={handleSubmit}
              buttonProps={{ children: 'Reset Password' }}
              submittingText="Resetting Password..."
              errorText={hasApiErrors ? 'Problem resetting password' : 'Resolve errors'}
              ButtonContainer={ButtonContainer}
            >
              <FormErrors errors={generalErrors} />
              <FormField>
                <TextField
                  inputComponent={
                    <div style={{ position: 'relative' }} className={styles['password-input']}>
                      <TextInput
                        id="password"
                        type={showPassword ? TextFieldTypes.text : TextFieldTypes.password}
                        name="password"
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className={styles['password-toggle']}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        aria-pressed={showPassword}
                      >
                        {showPassword ? <SvgEyeFill /> : <SvgEyeSlashFill />}
                      </button>
                    </div>
                  }
                  label="Password"
                  error={getFieldError('password')}
                />
              </FormField>

              <FormField>
                <TextField
                  inputComponent={
                    <div style={{ position: 'relative' }} className={styles['password-input']}>
                      <TextInput
                        id="passwordConfirmation"
                        type={showConfirmPassword ? TextFieldTypes.text : TextFieldTypes.password}
                        name="passwordConfirmation"
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <button
                        type="button"
                        onClick={toggleConfirmPasswordVisibility}
                        className={styles['password-toggle']}
                        aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                        aria-pressed={showConfirmPassword}
                      >
                        {showConfirmPassword ? <SvgEyeFill /> : <SvgEyeSlashFill />}
                      </button>
                    </div>
                  }
                  label="Confirm Password"
                  error={getFieldError('passwordConfirmation')}
                />
              </FormField>
              <div className={styles['password-criteria']}>
                {passwordConstraintContent?.map(child => (
                  <div
                    key={child.name}
                    className={matchedCriteria?.includes(child.name) ? styles.matched : styles.default}
                  >
                    <p>{child.message}</p>
                  </div>
                ))}
              </div>
            </FormLoader>
          )
        }}
      </Formik>
    </div>
  )
}

export default ResetPasswordForm
